export const recentWorkTilesTokens = {
  colors: {
    background: "#f7f9fc",
    surface: "#ffffff",
    text: "#071735",
    textSoft: "#26334d",
    textMuted: "#56647a",
    accent: "#0b5ed7",
    accentDark: "#061f4a",
    accentSoft: "rgba(11, 94, 215, 0.12)",
    border: "rgba(7, 23, 53, 0.1)",
    divider: "rgba(7, 23, 53, 0.14)",
    chipBackground: "rgba(7, 23, 53, 0.05)",
    imageFallback: "#e8eef7",
    buttonText: "#ffffff",
    statusComplete: "#16a34a",
    statusCompleteSoft: "rgba(22, 163, 74, 0.12)",
    shadow: "0 18px 44px rgba(7, 23, 53, 0.1)",
    hoverShadow: "0 22px 54px rgba(7, 23, 53, 0.14)",
  },
  categoryPalettes: {
    electrical: {
      accent: "#0b5ed7",
      soft: "rgba(11, 94, 215, 0.12)",
    },
    plumbing: {
      accent: "#0e7490",
      soft: "rgba(14, 116, 144, 0.12)",
    },
    roofing: {
      accent: "#4f46e5",
      soft: "rgba(79, 70, 229, 0.12)",
    },
    renovation: {
      accent: "#be123c",
      soft: "rgba(190, 18, 60, 0.12)",
    },
    solar: {
      accent: "#059669",
      soft: "rgba(5, 150, 105, 0.12)",
    },
    maintenance: {
      accent: "#b45309",
      soft: "rgba(180, 83, 9, 0.13)",
    },
  },
  statusTones: {
    complete: {
      color: "#16a34a",
      soft: "rgba(22, 163, 74, 0.12)",
    },
    progress: {
      color: "#d97706",
      soft: "rgba(217, 119, 6, 0.12)",
    },
    booked: {
      color: "#0e7490",
      soft: "rgba(14, 116, 144, 0.12)",
    },
  },
  icons: {
    eyebrow: "star",
    media: "camera",
    beforeAfter: "image",
    categoryDefault: "wrench",
    electrical: "zap",
    plumbing: "droplet",
    roofing: "home",
    status: "checkCircle",
    location: "mapPin",
    date: "calendar",
    cta: "arrowRight",
    trustShield: "shieldCheck",
    trustCustomers: "users",
    trustExperience: "calendarDays",
    trustGuarantee: "award",
  },
  radius: {
    card: 8,
    mediaBadge: 6,
    pill: 999,
    button: 5,
  },
  sizes: {
    maxWidth: 1280,
    imageHeight: 238,
    tabletImageHeight: 220,
    mobileImageHeight: 156,
    trustIcon: 54,
    mobileTrustIcon: 40,
  },
  spacing: {
    shellY: 58,
    shellX: 24,
    tabletShellY: 48,
    tabletShellX: 24,
    mobileShellY: 36,
    mobileShellX: 16,
    cardGap: 24,
    tabletCardGap: 22,
    mobileCardGap: 16,
    cardContentX: 20,
    cardContentY: 20,
  },
  typography: {
    fontFamily:
      'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    eyebrow: {
      fontSize: 13,
      mobileFontSize: 11,
      lineHeight: 1,
      fontWeight: 850,
      letterSpacing: 0,
      textTransform: "uppercase" as const,
    },
    title: {
      fontSize: 58,
      tabletFontSize: 44,
      mobileFontSize: 32,
      lineHeight: 1.04,
      mobileLineHeight: 1.1,
      fontWeight: 900,
      letterSpacing: 0,
    },
    description: {
      fontSize: 18,
      tabletFontSize: 16,
      mobileFontSize: 14,
      lineHeight: 1.45,
      fontWeight: 500,
      letterSpacing: 0,
    },
    mediaBadge: {
      fontSize: 12,
      mobileFontSize: 10,
      lineHeight: 1,
      fontWeight: 850,
      letterSpacing: 0,
      textTransform: "uppercase" as const,
    },
    category: {
      fontSize: 12,
      mobileFontSize: 10,
      lineHeight: 1,
      fontWeight: 850,
      letterSpacing: 0,
      textTransform: "uppercase" as const,
    },
    cardTitle: {
      fontSize: 23,
      tabletFontSize: 20,
      mobileFontSize: 16,
      lineHeight: 1.12,
      fontWeight: 850,
      letterSpacing: 0,
    },
    meta: {
      fontSize: 14,
      mobileFontSize: 11,
      lineHeight: 1.25,
      fontWeight: 600,
      letterSpacing: 0,
    },
    body: {
      fontSize: 15,
      lineHeight: 1.55,
      fontWeight: 500,
      letterSpacing: 0,
    },
    chip: {
      fontSize: 12,
      mobileFontSize: 10,
      lineHeight: 1,
      fontWeight: 750,
      letterSpacing: 0,
    },
    cta: {
      fontSize: 14,
      mobileFontSize: 12,
      lineHeight: 1,
      fontWeight: 850,
      letterSpacing: 0,
    },
    trustTitle: {
      fontSize: 15,
      mobileFontSize: 12,
      lineHeight: 1.18,
      fontWeight: 850,
      letterSpacing: 0,
    },
    trustText: {
      fontSize: 12,
      mobileFontSize: 10,
      lineHeight: 1.35,
      fontWeight: 500,
      letterSpacing: 0,
    },
  },
  motion: {
    card: "box-shadow 180ms ease, transform 180ms ease, border-color 180ms ease",
    image: "transform 420ms ease, filter 420ms ease",
    cta: "transform 180ms ease, background-color 180ms ease",
  },
  defaults: {
    eyebrow: "Real jobs. Real results.",
    title: "Recent Work",
    description:
      "Take a look at some of our recently completed jobs. Quality workmanship, happy customers, and results you can trust.",
    ctaLabel: "View Job",
    statusLabel: "Completed",
    mediaLabel: "Photos from site",
    trustItems: [
      {
        id: "licensed",
        label: "Fully Licensed & Insured",
        description: "Peace of mind guaranteed",
        iconToken: "shieldCheck",
      },
      {
        id: "customers",
        label: "500+ Happy Customers",
        description: "5 star reviews across QLD",
        iconToken: "users",
      },
      {
        id: "experience",
        label: "10+ Years Experience",
        description: "Delivering quality every time",
        iconToken: "calendarDays",
      },
      {
        id: "guarantee",
        label: "Workmanship Guarantee",
        description: "We stand by our work",
        iconToken: "award",
      },
    ],
  },
} as const;

export type RecentWorkTilesColorToken = keyof typeof recentWorkTilesTokens.colors;
export type RecentWorkTilesCategoryTone =
  keyof typeof recentWorkTilesTokens.categoryPalettes;
export type RecentWorkTilesStatusTone =
  keyof typeof recentWorkTilesTokens.statusTones;
